/**
 * Creator-scoped completion / failure toasts for AI Scribe recordings
 * (LF-3712, FE-9, D-32 + functional spec §7).
 *
 * Recording feedback is delivered as ephemeral `sonner` toasts — there is no
 * notification centre / inbox. Two call sites fire these (per the chosen
 * design): the open session page fires immediately off its 5s detail poll when
 * a recording transitions out of processing; a global catch-all
 * (`RecordingCompletionToaster`) fires off the firm-wide notifications poll
 * when the lawyer has navigated away. Both route through here, and a
 * sessionStorage-backed dedup keyed by `<kind>:<recordingId>` guarantees a
 * single toast per completion regardless of which path observes it first.
 */

'use client';

import { toast } from '@/lib/toast';

/** Backend `EventType` values, as they arrive on the notification `type`. */
export const RECORDING_PROCESSED_NOTIFICATION_TYPE = 'recording.processed';
export const RECORDING_FAILED_NOTIFICATION_TYPE = 'recording.failed';

const DEDUP_STORAGE_KEY = 'scribe:toasted-completions';

function readDedupSet(): Set<string> {
  if (typeof window === 'undefined') return new Set();
  try {
    const raw = window.sessionStorage.getItem(DEDUP_STORAGE_KEY);
    return new Set<string>(raw ? (JSON.parse(raw) as string[]) : []);
  } catch {
    return new Set();
  }
}

function persistDedupSet(set: Set<string>): void {
  if (typeof window === 'undefined') return;
  try {
    window.sessionStorage.setItem(
      DEDUP_STORAGE_KEY,
      JSON.stringify(Array.from(set))
    );
  } catch {
    // sessionStorage unavailable (private mode / quota) — dedup degrades to
    // per-render only; a duplicate toast is preferable to a thrown error.
  }
}

/** Claim a dedup key. Returns false if it was already toasted this session. */
function claim(key: string): boolean {
  const set = readDedupSet();
  if (set.has(key)) return false;
  set.add(key);
  persistDedupSet(set);
  return true;
}

interface RecordingToastArgs {
  recordingId: number;
  /** Recording title — used as the toast subtitle on the ready toast. */
  title?: string | null;
  /** Navigate to the recording's session page (the "View recording" action). */
  onView: () => void;
}

/** "Recording ready" — subtitle is the recording title (prototype 04). */
export function notifyRecordingReady({
  recordingId,
  title,
  onView
}: RecordingToastArgs): void {
  if (!claim(`ready:${recordingId}`)) return;
  toast.success('Recording ready', {
    description: title || undefined,
    action: { label: 'View recording', onClick: onView }
  });
}

/** "Couldn't process recording" — reassuring subtitle (prototype 04). */
export function notifyRecordingFailed({
  recordingId,
  onView
}: RecordingToastArgs): void {
  if (!claim(`failed:${recordingId}`)) return;
  toast.error("Couldn't process recording", {
    description: 'Your audio is safe.',
    action: { label: 'View recording', onClick: onView }
  });
}
